import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, Monitor, Volume2, Mic, MicOff, Minimize2, PhoneOff, RefreshCw, Video as VideoIcon, VideoOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MediaVideo = ({ stream, muted = false, className, mirrored = false }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream || null; }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} style={mirrored ? { transform: 'scaleX(-1)' } : undefined} />;
};

const MediaAudio = ({ stream }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream || null; }, [stream]);
  return <audio ref={ref} autoPlay aria-label="Remote call audio" className="sr-only" />;
};

const CallSurfaceContent = ({ callState, otherPartyName }) => {
  const { call, localStream, remoteStream, connectionState, muted, cameraOff, error, accepting, acceptCall, declineCall, endCall, toggleMute, toggleCamera, switchCamera, isTerminal, sharingScreen, facingMode, toggleScreenShare } = callState;
  const callId = call?.call_id;
  const dialogRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [swapped, setSwapped] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [actionError, setActionError] = useState(null);
  useEffect(() => {
    if (!call || call.status !== 'accepted') return undefined;
    const acceptedAt = Date.parse(call.accepted_at || '') || Date.now();
    const update = () => setElapsed(Math.max(0, Math.floor((Date.now() - acceptedAt) / 1000)));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [call]);
  useEffect(() => { setMinimized(false); setSwapped(false); setActionError(null); }, [call?.call_id]);
  useEffect(() => {
    if (!callId || minimized || isTerminal) return undefined;
    const previous = document.activeElement;
    const dialog = dialogRef.current;
    const controls = () => Array.from(dialog?.querySelectorAll('button:not(:disabled)') || []);
    controls()[0]?.focus();
    const trap = (event) => {
      if (event.key !== 'Tab') return;
      const items = controls();
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    dialog?.addEventListener('keydown', trap);
    return () => { dialog?.removeEventListener('keydown', trap); if (previous?.isConnected) previous.focus(); };
  }, [callId, call?.status, accepting, minimized, isTerminal]);
  if (!call) return null;
  const isIncoming = call.direction === 'incoming' && call.status === 'ringing';
  if (isIncoming) return (
    <section ref={dialogRef} role="dialog" aria-modal="true" aria-label={`Incoming ${call.media_type} call from ${otherPartyName}`} className="call-stage">
      <div className="call-identity"><div className="call-avatar" aria-hidden="true">{otherPartyName?.slice(0, 1)}</div><h2>{otherPartyName} is calling</h2></div>
      {error && <p role="alert" className="mt-2 text-sm text-amber-300">{error}</p>}
      <p className="mt-1 text-sm text-slate-300">{call.media_type === 'video' ? 'Video' : 'Audio'} call · {call.campaign_id ? 'Campaign conversation' : 'Conversation'}</p>
      {accepting && <p role="status" className="mt-2 text-sm text-slate-300">Waiting for microphone and camera permission…</p>}
      <div className="call-controls">
        <Button size="icon" onClick={acceptCall} disabled={accepting} aria-busy={Boolean(accepting)} aria-label={`Accept ${call.media_type} call`}>{call.media_type === 'video' ? <VideoIcon /> : <Mic />}</Button>
        <Button variant="outline" onClick={declineCall} aria-label="Decline call"><PhoneOff /></Button>
      </div>
    </section>
  );
  if (isTerminal) return (
    <section role="status" aria-label="Call ended" className="fixed inset-x-3 top-3 z-[80] rounded-2xl bg-slate-950 p-4 text-white shadow-xl">
      <p className="font-semibold">Call {call.status === 'ended' ? 'ended' : call.status}</p>
      <p className="mt-1 text-xs text-slate-300">{call.terminal_reason || 'The call is no longer active.'}</p>
    </section>
  );
  if (minimized) return (
    <section aria-label="Minimized active call" className="fixed right-3 top-3 z-[80] flex items-center gap-3 rounded-full bg-slate-950 px-4 py-2 text-white shadow-xl">
      <MediaAudio stream={remoteStream} />
      <span className="text-sm font-medium">{otherPartyName} · {connectionState}</span>
      <Button variant="ghost" size="icon" className="text-white" onClick={() => setMinimized(false)} aria-label="Expand call"><Maximize2 /></Button>
      <Button variant="destructive" size="icon" onClick={endCall} aria-label="End call"><PhoneOff /></Button>
    </section>
  );
  return (
    <section ref={dialogRef} role="dialog" aria-modal="true" aria-label="Active call" className={`call-stage ${call.media_type === 'video' ? 'is-video' : ''}`}>
      {call.media_type === 'audio' && <MediaAudio stream={remoteStream} />}
      {call.media_type === 'video' && <>
        <MediaAudio stream={remoteStream} />
        <MediaVideo stream={!remoteStream || swapped ? localStream : remoteStream} muted className="call-remote-video" mirrored={(!remoteStream || swapped) && facingMode !== 'environment' && !sharingScreen} />
        {remoteStream && <button className="call-preview" onClick={() => setSwapped((value) => !value)} aria-label="Swap large and small video"><MediaVideo stream={swapped ? remoteStream : localStream} muted className="call-local-video" mirrored={!swapped && facingMode !== 'environment' && !sharingScreen} /></button>}
      </>}
      <div className="call-identity">
        {call.media_type === 'audio' && <div className="call-avatar" aria-hidden="true">{otherPartyName?.slice(0, 1)}</div>}
        <h2>{otherPartyName}</h2><p>{call.status === 'ringing' ? 'Calling…' : connectionState} · {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</p>
      </div>
      <Button variant="ghost" size="icon" className="call-minimize text-white" onClick={() => setMinimized(true)} aria-label="Minimize call"><Minimize2 /></Button>
      <div className="call-controls">
        {call.media_type === 'video' && <Button size="icon" onClick={toggleCamera} aria-pressed={cameraOff} aria-label={cameraOff ? 'Turn camera on' : 'Turn camera off'}>{cameraOff ? <VideoOff /> : <VideoIcon />}</Button>}
        <Button size="icon" onClick={toggleMute} aria-pressed={muted} aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}>{muted ? <MicOff /> : <Mic />}</Button>
        <Button variant="destructive" size="icon" onClick={endCall} aria-label="End call"><PhoneOff /></Button>
        {call.media_type === 'audio' && <Button size="icon" disabled aria-label="Speaker output controlled by device" title="Choose speaker output in your browser or device settings"><Volume2 /></Button>}
        {call.media_type === 'video' && <Button size="icon" disabled={!navigator.mediaDevices?.getDisplayMedia || call.status !== 'accepted'} aria-label={sharingScreen ? 'Stop sharing screen' : 'Share screen'} aria-pressed={Boolean(sharingScreen)} title={navigator.mediaDevices?.getDisplayMedia ? 'Share your screen' : 'Screen capture is unavailable in this browser'} onClick={() => toggleScreenShare().then(() => setActionError(null)).catch((cause) => setActionError(cause.message))}><Monitor /></Button>}
        {call.media_type === 'video' && <Button size="icon" disabled={Boolean(sharingScreen)} onClick={() => switchCamera().then(() => setActionError(null)).catch((cause) => setActionError(cause.message))} aria-label="Switch camera"><RefreshCw /></Button>}
      </div>
      <p className="call-output-note">{call.media_type === 'audio' ? 'Speaker output follows your browser or device audio setting.' : navigator.mediaDevices?.getDisplayMedia ? 'Tap the small video to swap views.' : 'Screen capture is unavailable in this browser.'}</p>
      {(error || actionError) && <p role="status" className="call-error">{actionError || error}</p>}
    </section>
  );
};

export const CallSurface = (props) => props.callState.call
  ? createPortal(<CallSurfaceContent {...props} />, document.body)
  : <CallSurfaceContent {...props} />;

export default CallSurface;
